export type Role = "owner" | "admin" | "member" | "viewer";

export const RESOURCES: Record<string, string[]> = {
	members: ["invite", "remove", "update-role", "transfer-ownership"],
	settings: ["read", "update"],
	data: ["create", "read", "update", "delete"],
	profile: ["update"],
	projects: ["create", "read", "update", "delete"],
	webhooks: ["create", "read", "update", "delete"],
	"api-keys": ["create", "read", "delete"],
};

export const ROLE_STATEMENTS: Record<Role, Record<string, string[]>> = {
	viewer: {
		data: ["read"],
		settings: ["read"],
		projects: ["read"],
	},
	member: {
		data: ["create", "read", "update", "delete"],
		settings: ["read"],
		profile: ["update"],
		projects: ["create", "read", "update", "delete"],
	},
	admin: {
		data: ["create", "read", "update", "delete"],
		settings: ["read", "update"],
		members: ["invite"],
		profile: ["update"],
		projects: ["create", "read", "update", "delete"],
		webhooks: ["create", "read", "update", "delete"],
		"api-keys": ["create", "read", "delete"],
	},
	owner: {
		data: ["create", "read", "update", "delete"],
		settings: ["read", "update"],
		members: ["invite", "remove", "update-role", "transfer-ownership"],
		profile: ["update"],
		projects: ["create", "read", "update", "delete"],
		webhooks: ["create", "read", "update", "delete"],
		"api-keys": ["create", "read", "delete"],
	},
};

export type TierId = "free" | "pro" | "enterprise";

/**
 * Tier gates: maps (resource, action) pairs that are restricted by tier.
 * If a resource/action is not listed here, it is allowed on all tiers.
 * The value is the set of tier IDs that ALLOW the action.
 */
const TIER_GATES: Record<string, Record<string, Set<TierId>>> = {
	settings: {
		update: new Set<TierId>(["pro", "enterprise"]),
	},
};

export function checkPermission(
	role: string | null | undefined,
	tier: string | null | undefined,
	resource: string,
	action: string,
): boolean {
	if (!role || !(role in ROLE_STATEMENTS)) {
		return false;
	}

	const statements = ROLE_STATEMENTS[role as Role];
	const allowedActions = statements[resource];
	if (!allowedActions || !allowedActions.includes(action)) {
		return false;
	}

	// Check tier gate if one exists for this resource/action
	const resourceGate = TIER_GATES[resource];
	if (resourceGate) {
		const actionGate = resourceGate[action];
		if (actionGate) {
			const effectiveTier = (tier ?? "free") as TierId;
			if (!actionGate.has(effectiveTier)) {
				return false;
			}
		}
	}

	return true;
}

/**
 * Convenience wrapper for client-side use where tier is not available.
 * Calls checkPermission with tier=undefined (always passes tier gate).
 */
export function getEffectiveTier(
	planTier: string,
	gracePeriodEnd: string | null,
): string {
	if (gracePeriodEnd && new Date() > new Date(gracePeriodEnd)) {
		return "free";
	}
	return planTier;
}

export function hasPermission(
	role: string | null | undefined,
	resource: string,
	action: string,
): boolean {
	return checkPermission(role, undefined, resource, action);
}
